import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  User, 
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Heart,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const AuthPage = ({ mode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLogin(mode === 'login');
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Sparkles, text: "Análise por IA avançada" },
    { icon: Shield, text: "100% seguro e privado" },
    { icon: Zap, text: "Resultados instantâneos" },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Left Side - Hero Section (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top, #0F766E 0%, #083D3A 60%, #0A0F14 100%)' }}>
          <div className="absolute inset-0 scanner-grid opacity-25" />
          {/* Animated scan line */}
          <motion.div
            className="absolute left-0 right-0 h-[2px] z-10"
            style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', opacity: 0.5 }}
            animate={{ top: ['10%', '90%', '10%'] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full"
               style={{ background: 'radial-gradient(circle, rgba(212,162,86,0.25) 0%, transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full"
               style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.2) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <Link to="/" className="mb-8">
            <img 
              src={LOGO_URL} 
              alt="AJ HealthMap" 
              className="h-40 w-auto object-contain drop-shadow-2xl"
            />
          </Link>
          
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-6 self-start"
               style={{ background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.3)' }}>
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span className="text-cyan-100 text-xs font-bold tracking-wider uppercase">Tecnologia IA</span>
          </div>

          <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
            Descubra o que seus
            <span className="block" style={{ background: 'linear-gradient(135deg, #22D3EE 0%, #D4A256 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>pés revelam</span>
          </h1>
          
          <p className="text-lg text-white/80 mb-10 max-w-md">
            Escaneie a sola do pé com IA avançada e desbloqueie um mapa reflexológico personalizado.
          </p>
          
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                     style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.25)' }}>
                  <feature.icon className="w-6 h-6 text-cyan-300" />
                </div>
                <span className="text-white/90">{feature.text}</span>
              </div>
            ))}
          </div>
          
          {/* Trust badges */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-6 text-sm text-white/60">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-amber-400" />
                <span>+5.000 leituras</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                <span>98% satisfação</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-md mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="AJ HealthMap" 
                className="h-44 w-auto object-contain"
                data-testid="auth-logo"
              />
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-[#E8E4D9]/50 animate-fade-in-up">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#1C1C1E] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                {isLogin ? 'Bem-vindo de volta!' : 'Criar sua conta'}
              </h2>
              <p className="text-[#8E8E93]">
                {isLogin ? 'Entre para continuar sua jornada' : 'Comece sua jornada de bem-estar'}
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm animate-fade-in-up" data-testid="auth-error">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="animate-fade-in-up">
                  <Label htmlFor="name" className="text-[#48484A] mb-2 block font-medium">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E8E93]" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome completo"
                      className="pl-12 h-14 rounded-xl border-2 border-[#E8E4D9] focus:border-[#2E5C55] focus:ring-0 bg-[#FDFBF7] text-base"
                      required={!isLogin}
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-[#48484A] mb-2 block font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E8E93]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-12 h-14 rounded-xl border-2 border-[#E8E4D9] focus:border-[#2E5C55] focus:ring-0 bg-[#FDFBF7] text-base"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-[#48484A] mb-2 block font-medium">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E8E93]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 pr-12 h-14 rounded-xl border-2 border-[#E8E4D9] focus:border-[#2E5C55] focus:ring-0 bg-[#FDFBF7] text-base"
                    required
                    minLength={6}
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93] hover:text-[#2E5C55] transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="flex justify-end">
                  <Link 
                    to="/reset-password" 
                    className="text-sm text-[#2E5C55] hover:text-[#1A3A35] font-medium"
                    data-testid="forgot-password-link"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-white rounded-full text-lg font-bold transition-all hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #083D3A 0%, #0F766E 50%, #14B8A6 100%)', boxShadow: '0 8px 30px rgba(15,118,110,0.4)' }}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Entrar' : 'Criar conta'}
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="section-divider my-8">
              <span className="text-sm text-[#8E8E93] px-4 bg-white">ou</span>
            </div>

            {/* Toggle */}
            <div className="text-center">
              <p className="text-[#8E8E93]">
                {isLogin ? 'Não tem uma conta?' : 'Já tem uma conta?'}
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="ml-2 text-[#2E5C55] font-semibold hover:text-[#1A3A35] transition-colors"
                  data-testid="auth-toggle-btn"
                >
                  {isLogin ? 'Criar conta' : 'Entrar'}
                </button>
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-[#8E8E93] text-center mt-8 px-4">
            Ao continuar, você concorda com nossos{' '}
            <Link to="/termos" className="text-[#2E5C55] hover:underline">Termos de Uso</Link>
            {' '}e{' '}
            <Link to="/privacidade" className="text-[#2E5C55] hover:underline">Política de Privacidade</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
